import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Database, 
  Trash2, 
  Loader2,
  CheckCircle,
  FileText,
  AlertTriangle,
  FileCheck,
  Plug,
  Shield
} from 'lucide-react';
import api from '../lib/api';

export default function DemoModePage() {
  const [seeding, setSeeding] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [results, setResults] = useState(null);

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await api.post('/api/demo/seed-sample-data');
      setResults(response.data.results);
      toast.success('Demo data seeded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  const handleClearData = async () => {
    if (!window.confirm('Are you sure you want to clear all sample data? This cannot be undone.')) {
      return;
    }
    
    setClearing(true);
    try {
      await api.delete('/api/demo/clear-sample-data');
      setResults(null);
      toast.success('Sample data cleared');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear data');
    } finally {
      setClearing(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Demo Mode</h1>
          <p className="text-slate-500 mt-1">Populate your workspace with sample data for demonstrations</p>
        </div>

        {/* What Gets Created */}
        <Card>
          <CardHeader>
            <CardTitle>What Gets Created</CardTitle>
            <CardDescription>Sample data that will be added to your workspace</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">SOC 2 Framework</span>
                </div>
                <p className="text-sm text-slate-600">49 controls with varied compliance statuses (60% compliant)</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  <span className="font-medium">4 Policies</span>
                </div>
                <p className="text-sm text-slate-600">Security, Access Control, Incident Response, Data Retention</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <span className="font-medium">5 Risks</span>
                </div>
                <p className="text-sm text-slate-600">Unauthorized Access, Phishing, Vendor Breach, Downtime, Ransomware</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <FileCheck className="w-5 h-5 text-purple-600" />
                  <span className="font-medium">6 Evidence Items</span>
                </div>
                <p className="text-sm text-slate-600">AWS configs, GitHub settings, training reports, pen test results</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <Plug className="w-5 h-5 text-indigo-600" />
                  <span className="font-medium">3 Connectors</span>
                </div>
                <p className="text-sm text-slate-600">AWS Production, GitHub Org, Okta SSO (demo configurations)</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <Database className="w-5 h-5 text-slate-600" />
                  <span className="font-medium">Control Updates</span>
                </div>
                <p className="text-sm text-slate-600">50 controls updated with realistic compliance statuses</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={handleSeedData} 
                disabled={seeding}
                size="lg"
              >
                {seeding ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Seeding Data...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    Seed Demo Data
                  </>
                )}
              </Button>
              
              <Button 
                onClick={handleClearData} 
                disabled={clearing}
                variant="destructive"
                size="lg"
              >
                {clearing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear Sample Data
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {results && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Seeding Complete
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {results.seeded && results.seeded.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>{item}</span>
                  </div>
                ))}
                {results.errors && results.errors.length > 0 && (
                  <div className="mt-4 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm font-medium text-red-800">Errors:</p>
                    {results.errors.map((error, index) => (
                      <p key={index} className="text-sm text-red-600">{error}</p>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tips */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-medium text-blue-900 mb-2">Demo Tips</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• After seeding, visit the Dashboard to see realistic compliance scores</li>
              <li>• Check the Risk Register to see sample risks with AI assessment potential</li>
              <li>• View the Evidence Lab to see different evidence types and statuses</li>
              <li>• Use Control Mapping to demonstrate cross-framework compliance</li>
              <li>• Generate a PDF report from the Audit Portal for a professional deliverable</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
